<template>
    <div class="bg-white p-6 shadow-md rounded-lg">
        <h2 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <font-awesome-icon :icon="['fas', 'calendar-week']" class="text-blue-600 mr-2" />
            Day Distribution
        </h2>

        <!-- Summary Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div class="bg-blue-50 p-3 rounded-lg">
                <p class="text-xs text-blue-600 font-medium">Weekdays</p>
                <p class="text-lg font-bold text-blue-800">{{ getWeekdayClasses() }}</p>
            </div>
            <div class="bg-green-50 p-3 rounded-lg">
                <p class="text-xs text-green-600 font-medium">Weekends</p>
                <p class="text-lg font-bold text-green-800">{{ getWeekendClasses() }}</p>
            </div>
            <div class="bg-yellow-50 p-3 rounded-lg">
                <p class="text-xs text-yellow-600 font-medium">Busiest Day</p>
                <p class="text-lg font-bold text-yellow-800">{{ getMostBusyDay() }}</p>
            </div>
            <div class="bg-purple-50 p-3 rounded-lg">
                <p class="text-xs text-purple-600 font-medium">Quietest Day</p>
                <p class="text-lg font-bold text-purple-800">{{ getLeastBusyDay() }}</p>
            </div>
        </div>

        <div v-if="hasData">
            <apexchart type="bar" height="350" :options="chartOptions" :series="chartSeries" />
        </div>
        <div v-else class="text-gray-500 text-center py-16">
            <div class="text-4xl mb-2">📅</div>
            <div>There is no data to show</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "DayDistributionChart",
    props: {
        data: {
            type: Object,
            required: true
        },
        powerBiColors: {
            type: Array,
            required: true
        }
    },
    computed: {
        hasData() {
            return this.data && this.data.days && this.data.days.length > 0;
        },
        chartSeries() {
            return [{
                name: 'Classes',
                data: this.getDayCounts()
            }];
        },
        chartOptions() {
            const labels = this.getDayNames();
            const colors = this.getDayColors(labels);

            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                        endingShape: 'rounded',
                        distributed: false
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toString();
                    }
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    categories: labels,
                    labels: {
                        style: {
                            fontSize: '12px'
                        }
                    },
                    title: {
                        text: 'Days of the Week'
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Classes'
                    }
                },
                fill: {
                    opacity: 1,
                    colors: colors
                },
                tooltip: {
                    y: {
                        formatter: function (val, { dataPointIndex, w }) {
                            const dayName = w.config.xaxis.categories[dataPointIndex];
                            const percentage = this.getDayPercentage(dayName);
                            return `${val} classes (${percentage}%)`;
                        }.bind(this)
                    }
                },
                colors: colors
            };
        }
    },
    methods: {
        getWeekdayClasses() {
            if (this.data && this.data.summary && this.data.summary.weekday_classes !== undefined) {
                return this.data.summary.weekday_classes;
            }
            return 0;
        },
        getWeekendClasses() {
            if (this.data && this.data.summary && this.data.summary.weekend_classes !== undefined) {
                return this.data.summary.weekend_classes;
            }
            return 0;
        },
        getMostBusyDay() {
            if (this.data && this.data.summary && this.data.summary.most_busy_day) {
                return this.data.summary.most_busy_day.day_name;
            }
            return 'N/A';
        },
        getLeastBusyDay() {
            if (this.data && this.data.summary && this.data.summary.least_busy_day) {
                return this.data.summary.least_busy_day.day_name;
            }
            return 'N/A';
        },
        getDayNames() {
            if (this.data && this.data.days && Array.isArray(this.data.days)) {
                return this.data.days.map(item => item.day_name);
            }
            return [];
        },
        getDayCounts() {
            if (this.data && this.data.days && Array.isArray(this.data.days)) {
                return this.data.days.map(item => item.class_count);
            }
            return [];
        },
        getDayPercentage(dayName) {
            if (this.data && this.data.days && Array.isArray(this.data.days)) {
                const day = this.data.days.find(item => item.day_name === dayName);
                return day ? day.percentage : 0;
            }
            return 0;
        },
        getDayColors(labels) {
            const colorMap = {
                'Monday': '#3498DB',
                'Tuesday': '#E74C3C',
                'Wednesday': '#F39C12',
                'Thursday': '#2ECC71',
                'Friday': '#9B59B6',
                'Saturday': '#1ABC9C',
                'Sunday': '#E67E22'
            };

            return labels.map(label => colorMap[label] || '#95A5A6');
        }
    }
};
</script>

<style scoped>
/* Custom styles for better visual appeal */
.apexcharts-legend {
    padding-top: 20px;
}
</style>
